import * as v0 from '../survey'
import { CompileExpressionToSQL, findFields } from './expr_to_sql';
import { CompileExpressionToJS } from './expr_to_js';
import { AirtableSurveyQuestion } from '../legacy/airtable';
import { FIELD_REF_REGEX, filterDict, flattenAnalyses, safeGetFormulaParents } from '../util';
import { CompileExpressionToEnglish } from './expr_to_english';
import w9 from '../documents/w9';
import * as slugid from 'slugid';
import { analyzeOutputSchema } from './questions/analyzeId.schema';
import { generateUuid } from './v0_to_legacy';
import { ReferenceBlocks } from './references';
import { BlockReference } from '../survey';

export type Add = {
  Question: (q: AirtableSurveyQuestion) => void,
  Translation: (text: v0.Content) => string,
  Choice: (choice: v0.Select['choices'][number]) => string,
  Role: (name: string, properties: string[]) => string,
  Section: (section: v0.Section & v0.CommonProperties, depth: number) => string,
  Depth: number
}

/**
 * This function traverses an object and creates a copy minus all properties with shape { en: string, ... }
 *
 * This is useful because the 'content' properties of question objects represent the vast percentage of
 * the size of the objects, but often does not surve functional purposes in terms of the system.
 */
export function removeContent(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (value && typeof value === 'object') {
      if ('en' in value && typeof value.en === 'string') {
        continue; // i.e. remove this from the final object
      }

      if (Array.isArray(value)) {
        result[key] = value.map(item => typeof item === 'object' ? removeContent(item) : item);
      } else {
        result[key] = removeContent(value);
      }
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Utility type - for given types T and V, returns a union of all keys K of T for which T[K] = V 
 * https://stackoverflow.com/questions/54520676/in-typescript-how-to-get-the-keys-of-an-object-type-whose-values-are-of-a-given
 */
export type KeysMatching<T, V> = { [K in keyof T]-?: T[K] extends V ? K : never }[keyof T];

export type DistroComponent = v0.Block | v0.CollectionComponent;

/**
 * A component that can be referenced, specifically excluding Reference types.
 */
export type ReferenceableComponent = Exclude<DistroComponent, BlockReference>;

/**
 * Used by DependencyNode. Represents a connection between the DependencyNode and one of its parents or children.
 */
export type Dependency = {
  field: v0.TargetFieldRef, // aka string
  type: 'formula' | 'content' | 'conditional'
  // | 'notif' | 'payment'
  // fromKind?: DistroComponent['kind'],
}

/**
 * A unit within a dependency graph.
 * Correlates closely to a single component, and id is typically that components main target field.
 */
export type DependencyNode = {
  id: v0.TargetFieldRef, // aka string,
  parents: Dependency[],
  children: Dependency[],
  component?: ReferenceableComponent | { kind: '(Derived)', targetField: string },
}

export type DependencyMap = Map<v0.TargetFieldRef, DependencyNode>;

/**
 * All Distro components in the TranslatorMap will implement these functions via the TRANSLATORS object.
 */
type Translator<C> = {
  /**
   * How to convert this Distro component into the legacy AirTable format
   */
  addFunc: (component: C, add: Add) => void,
  /**
   * What additional fields this component adds to the system, defined as either a StandaloneSlug
   * where the field is exactly what is in the slug, or a SuffixSlug where new field is the combination
   * of the main target field and the specified suffix.
   *
   * Typically, a component has a single main target field (often component.targetField)
   * where it will save its data. Some components save data in additional fields, such as
   * Address which saves the user input in its targetField but then splits that information
   * and stores pieces of it in other fields such as targetField + '_street' and targetField + '_zip'
   */
  getSlugs: (component: C) => { slugs: (v0.StandaloneSlug | v0.SuffixSlug)[] }
  // TODO(Riley): why is this an array?
  /**
   * A BooleanExpr indicating whether or not the component has had all of its inputs
   * satisfied. For most "ordinary" components, this will be something like:
   * getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
   */
  getRequirements: (component: C) => v0.BooleanExpr[],
  /**
   * Fills in the provided DependencyMap with the target field ancestors and descendants of the component,
   * also tracking what type the linkage is, e.g. 'formula', or 'conditional', or 'content.
   *
   * For container components like Section and Collection, this will recursively map the dependencies of
   * all of their children
   */
  calculateDependencies: (component: C, dependencyMap: DependencyMap) => void,
}

// Ensures that component types are consistent within a Translator.
export type TranslatorMap = {
  [K in DistroComponent['kind']]: Translator<Extract<DistroComponent, { kind: K }>>;
}

/**
 * Traverses the content provided and returns a list of 'content' type dependencies for all
 * fields found within.
 */
function getFieldReferences(content: v0.ConditionalContentList | v0.RichText | string): Dependency[] {
  const fields = new Set<string>();

  if (typeof content === 'string') {
    [...content.matchAll(FIELD_REF_REGEX)].forEach((match) => {
      // Strip leading $
      fields.add(match[0].substring(1));
    });
  } else if (Array.isArray(content)) {
    // This is Conditional content
    // TODO(Riley): handle this - is this still needed now that findContentAndAddDeps exists?
    // TODO(Riley): how do we want to think of/denote dependencies for fields used in the conditional
    // of content... are they conditional or content?
  } else {
    // TODO(Riley): in prod, we may want to have this just look at the 'en' key to speed things up,
    // though that will occasionally miss language-specific variables
    (Object.keys(content) as (keyof typeof content)[]).forEach((key) => {
      if (key.startsWith('_')) {
        // Covers _id and _trackedObjInfo
        return;
      }
      [...content[key]?.matchAll(FIELD_REF_REGEX) || []].forEach((match) => {
        // Strip leading $
        fields.add(match[0].substring(1))
      });
    });
  }

  return [...fields].map(field => { return { field, type: 'content' } });
}

/**
 * Collects and returns fields from slugs an "ordinary" component, i.e. one that has a single, main target field.
 */
function getFieldsFromSlugs(component: ReferenceableComponent, mainTargetField: string) {
  const { slugs } = TRANSLATORS[component.kind].getSlugs(component as any);
  // TODO(Riley): suffix slugs don't really exist, so the below doesn't work. Figure this out.
  // Maybe something like https://app.graphite.dev/github/pr/aidkitorg/aidkit/4936/Add-getDependencies-to-questions.ts#comment-PRRC_kwDOEXZ8E855GR1Q
  // For now, we instead use a hack for now based on startsWith('_')
  // return [...slugs.map((slug) => slug.__slug === 'suffix' ? mainTargetField + slug : slug)];
  return [...slugs.map((slug) => slug.startsWith('_') ? mainTargetField + slug : slug)];
}

/**
 * Traverses an abitrary component and calls getFieldReferences on any property that is a string
 * or looks like a Text or RichText object. All fields found will be added as 'content' parents to the
 * provided componentTargetFields.
 * @param componentTargetFields - this will generally be a single item, but some questions have multiple target fields of their own.
 */
function findContentAndAddDeps(component: ReferenceableComponent, componentTargetFields: string[], dependencyMap: DependencyMap) {
  function traverse(node: any) {
    if (typeof node === 'object') {
      if (Array.isArray(node)) {
        node.forEach(val => traverse(val))
      } else if ('en' in node) {
        getFieldReferences(node).forEach(dep => {
          componentTargetFields.forEach(targetField => {
            addParent(targetField, dep, dependencyMap);
          });
        });
      } else {
        Object.keys(node).forEach(prop => traverse(node[prop]));
      }
    } else if (typeof node === 'string') {
      getFieldReferences(node).forEach(dep => {
        componentTargetFields.forEach(targetField => {
          addParent(targetField, dep, dependencyMap);
        });
      });
    }
  }

  traverse(component);
}

/**
 * Utility function for "ordinary" components which finds and adds dependencies for all content, conditionals, and slugs.
 *
 * In many cases this is all that is needed to calculateDependencies for a component.
 */
function addConditionsAndSlugsAndContentDeps(
  params: {
    component: ReferenceableComponent,
    mainTargetField: string,
    otherTargetFields?: string[],
    dependencyMap: DependencyMap
  }
) {
  const { component, mainTargetField, otherTargetFields, dependencyMap } = params;
  if (!dependencyMap.has(mainTargetField)) {
    dependencyMap.set(mainTargetField, { id: mainTargetField, parents: [], children: [] });
  }
  dependencyMap.get(mainTargetField)!.component = component;
  if ('conditionalOn' in component && component.conditionalOn) {
    const conditionParents = typeof component.conditionalOn == 'string'
      // ? await getAllFormulaParents(component.conditionalOn)
      // : await getAllFormulaParents(CompileExpressionToJS(component.conditionalOn));
      ? safeGetFormulaParents(component.conditionalOn)
      : safeGetFormulaParents(CompileExpressionToJS(component.conditionalOn));

    (new Set(conditionParents)).forEach((p) => {
      addParent(mainTargetField, { field: p, type: 'conditional' }, dependencyMap);
      (otherTargetFields ?? []).forEach(otherField => {
        addParent(otherField, { field: p, type: 'conditional' }, dependencyMap);
      });
    });
  }
  findContentAndAddDeps(
    component,
    [mainTargetField, ...getFieldsFromSlugs(component, mainTargetField), ...(otherTargetFields ?? [])],
    dependencyMap);
  getFieldsFromSlugs(component, mainTargetField).forEach((slug) => {
    addChild(mainTargetField, { type: 'formula', field: slug }, dependencyMap);
    dependencyMap.get(slug)!.component = { kind: '(Derived)', targetField: slug };
  });
}

/**
 * Adds dependency to the dependencyMap as a child of targetField, meaning dependency depends on targetField.
 * Adds the inverse edge as well, i.e. targetfield as a parent of the dependency.
 */
function addChild(targetField: string, dependency: Dependency, dependencyMap: DependencyMap) {
  if (!dependencyMap.has(targetField)) {
    dependencyMap.set(targetField, { id: targetField, parents: [], children: [] });
  }
  dependencyMap.get(targetField)!.children.push({ type: dependency.type, field: dependency.field });
  // Add edge to destination node as well.
  if (!dependencyMap.has(dependency.field)) {
    dependencyMap.set(dependency.field, { id: dependency.field, parents: [], children: [] });
  }
  dependencyMap.get(dependency.field)!.parents.push({ type: dependency.type, field: targetField });
}

/**
 * Adds dependency to the dependencyMap as a parent of targetField, meaning targetField depends on dependency.
 * Adds the inverse edge as well, i.e. targetfield as a child of the dependency.
 */
function addParent(targetField: string, dependency: Dependency, dependencyMap: DependencyMap) {
  if (!dependencyMap.has(targetField)) {
    dependencyMap.set(targetField, { id: targetField, parents: [], children: [] });
  }
  dependencyMap.get(targetField)!.parents.push({ type: dependency.type, field: dependency.field });
  // Add edge to destination node as well.
  if (!dependencyMap.has(dependency.field)) {
    dependencyMap.set(dependency.field, { id: dependency.field, parents: [], children: [] });
  }
  dependencyMap.get(dependency.field)!.children.push({ type: dependency.type, field: targetField });
}

/**
 * If a @param virtualNode is passed in, it will be inserted between the condition and the subcomponents.
 * E.g. Conditional Block or Section
 * [Condition fields] -> ConditionBlock -> top level fields inside that ConditionalBlock
 */
function connectConditionsToTopLevelComponents(
  condition: string | v0.BooleanExpr,
  subComponents: v0.Block[],
  dependencyMap: DependencyMap,
  virtualNode?: DependencyNode,
) {
  const parents = typeof condition == 'string'
    // ? await getAllFormulaParents(condition)
    // : await getAllFormulaParents(CompileExpressionToJS(condition));
    ? safeGetFormulaParents(condition)
    : safeGetFormulaParents(CompileExpressionToJS(condition));
  // TODO(Riley): Should only the top-level components/fields of a conditional block depend on its condition fields? Probably,
  // that's how we do everything else, but how do we make that work...
  // If we can get the target fields of the top-level components... that could work. which we can do use getRequirements, though it's
  // kinda hacky...
  // Another option would be to add another translator function that returns the targetField(s) for a component, though it would largely
  // be boilerplate.
  // NOTE: This code is copied for the Sections component, and both should be kept in sync.
  let topLevelFields: string[] = [];
  // Collect deps from all subcomponents
  subComponents.forEach(subComponent => {
    const translator = TRANSLATORS[subComponent.kind as keyof typeof TRANSLATORS];
    if (!translator) return;

    translator.calculateDependencies(subComponent as any, dependencyMap);
    const requirements = translator.getRequirements(subComponent as any);
    // TODO(Riley): [0] is also a bit of a hack here, but why are those even an array to begin with?
    if (requirements.length > 0) {
      topLevelFields.push(...safeGetFormulaParents(CompileExpressionToJS(requirements[0])));
    }
  });

  // Insert the virtual node between the condition fields and the top-level fields of the subcomponents
  if (virtualNode) {
    parents.forEach(parentField => {
      addParent(virtualNode.id, { field: parentField, type: 'conditional' }, dependencyMap);
    });
    topLevelFields.forEach(topLevelField => {
      // TODO(Riley): conditional feels weird here, but what else would it be?
      addChild(virtualNode.id, { field: topLevelField, type: 'conditional' }, dependencyMap);
    });
  } else {
    // Connect the condition fields directly to the top-level-fields of the subcomponents
    parents.forEach(parentField => {
      topLevelFields.forEach((topLevelField) => {
        addParent(topLevelField, { field: parentField, type: 'conditional' }, dependencyMap);
      });
    });
  }
}

/**
 * A map of Distro component "kind"s to functions which can be performed given a component
 * of that type. More information can be found on the Translator<C> type, but briefly:
 * addFunc - translates the Distro component to an AirTable-style component by way of
 *           the "add" param.
 * getSlugs - returns an array of associated fields created by the component
 * getRequirements - returns a BooleanExpr which when computed indicates whether the
 *                   component has all its required user-input data
 * calculateDependencies - populates the "dependencyMap" with all fields referenced by or
 *                         directly created by the component
 */
export const TRANSLATORS: Omit<TranslatorMap, ReferenceBlocks> = {
  'Macro': {
    addFunc: (component, add: Add) => { },
    getSlugs: (component) => { return { slugs: [] } },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  },
  'AI Assistant Review Survey': {
    addFunc: (component, add: Add) => { },
    getSlugs: (component) => { return { slugs: [] } },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  },
  'Applicant Portal Page': {
    addFunc: (component, add: Add) => { },
    getSlugs: (component) => { return { slugs: [] } },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  },
  'Applicant Identities': {
    addFunc: (component, add: Add) => { },
    getSlugs: (component) => { return { slugs: [] } },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  },
  'Dashboard': {
    addFunc: (component, add: Add) => { },
    getSlugs: (component) => { return { slugs: [] } },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  },
  'Report': {
    addFunc: (component, add: Add) => { },
    getSlugs: (component) => { return { slugs: [] } },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  },
  'GiveCard Mailing': {
    addFunc: (component, add: Add) => { },
    getSlugs: (component) => { return { slugs: [] } },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      if (component.mailingAddress) {
        addParent(component.targetField, { field: component.mailingAddress, type: 'formula' }, dependencyMap);
      }

      // TODO(Riley): should this be formula or conditional? (or a third thing?) - make sure it aligns with InlineNotification
      const enabledQueryParents = component.condition.kind === 'SQL'
        // ? await getAllFormulaParents(component.formula)
        // : await getAllFormulaParents(CompileExpressionToJS(component.formula));
        // TODO(Riley): this is raw SQL, not JS, so we need to parse it using a SQL parser.
        ? [] // safeGetFormulaParents(component.condition.sql)
        : safeGetFormulaParents(CompileExpressionToJS(component.condition.expr));
      (new Set(enabledQueryParents)).forEach((p) => {
        addParent(component.targetField, { field: p, type: 'formula' }, dependencyMap);
      });

      if (component.higherSpendingLimit) {
        addParent(component.targetField, { field: component.higherSpendingLimit, type: 'formula' }, dependencyMap);
      }
      if (component.type) {
        addParent(component.targetField, { field: component.type, type: 'formula' }, dependencyMap);
      }
      if (component.contactField) {
        addParent(component.targetField, { field: component.contactField, type: 'formula' }, dependencyMap);
      }
    }
  },
  'USBank Card Mailing': {
    addFunc: (component, add: Add) => { },
    getSlugs: (component) => { return { slugs: [] } },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      if (component.mailingAddress) {
        addParent(component.targetField, { field: component.mailingAddress, type: 'formula' }, dependencyMap);
      }
      if (component.physicalAddress) {
        addParent(component.targetField, { field: component.physicalAddress, type: 'formula' }, dependencyMap);
      }

      // TODO(Riley): should this be formula or conditional? (or a third thing?) - make sure it aligns with InlineNotification
      const enabledQueryParents = component.condition.kind === 'SQL'
        // ? await getAllFormulaParents(component.formula)
        // : await getAllFormulaParents(CompileExpressionToJS(component.formula));
        // TODO(Riley): this is raw SQL, not JS, so we need to parse it using a SQL parser.
        ? [] // safeGetFormulaParents(component.condition.sql)
        : safeGetFormulaParents(CompileExpressionToJS(component.condition.expr));
      (new Set(enabledQueryParents)).forEach((p) => {
        addParent(component.targetField, { field: p, type: 'formula' }, dependencyMap);
      });

      if (component.contactField) {
        addParent(component.targetField, { field: component.contactField, type: 'formula' }, dependencyMap);
      }
      if (component.ssnField) {
        addParent(component.targetField, { field: component.ssnField, type: 'formula' }, dependencyMap);
      }
      if (component.birthDateField) {
        addParent(component.targetField, { field: component.birthDateField, type: 'formula' }, dependencyMap);
      }
    }
  },
  'Payment': {
    addFunc: (component, add: Add) => { },
    getSlugs: (component) => { return { slugs: [] } },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      // TODO(Riley): should this be formula or conditional? (or a third thing?) - make sure it aligns with InlineNotification
      const enabledQueryParents = component.condition.kind === 'SQL'
        // ? await getAllFormulaParents(component.formula)
        // : await getAllFormulaParents(CompileExpressionToJS(component.formula));
        // TODO(Riley): this is raw SQL, not JS, so we need to parse it using a SQL parser.
        ? [] // safeGetFormulaParents(component.condition.sql)
        : safeGetFormulaParents(CompileExpressionToJS(component.condition.expr));
      (new Set(enabledQueryParents)).forEach((p) => {
        addParent(component.targetField, { field: p, type: 'formula' }, dependencyMap);
      });

      addParent(component.targetField, { field: component.type, type: 'formula' }, dependencyMap);
      if (component.ledger && typeof component.ledger !== 'string') {
        addParent(component.targetField, { field: component.ledger.field, type: 'formula' }, dependencyMap);
      }
      if (component.cardIdField) {
        addParent(component.targetField, { field: component.cardIdField, type: 'formula' }, dependencyMap);
      }
      if (component.zelleContactField) {
        addParent(component.targetField, { field: component.zelleContactField, type: 'formula' }, dependencyMap);
      }
      if (component.higherSpendingLimit) {
        addParent(component.targetField, { field: component.higherSpendingLimit, type: 'formula' }, dependencyMap);
      }

      if (component.autoRetry) {
        (component.autoRetry.onFailure || []).forEach(onFailure => {
          const onFailureParents = safeGetFormulaParents(CompileExpressionToJS(onFailure.value));
          (new Set(onFailureParents)).forEach((p) => {
            addParent(onFailure.key, { field: p, type: 'formula' }, dependencyMap);
          });
          addParent(onFailure.key, { field: component.targetField, type: 'formula' }, dependencyMap);
        });

        if (component.autoRetry.disableRetryWhen) {
          const disableRetryCondParents = safeGetFormulaParents(CompileExpressionToJS(component.autoRetry.disableRetryWhen));
          (new Set(disableRetryCondParents)).forEach((p) => {
            // TODO(Riley): this should arguably apply to the onFailure fields, not the component.targetField
            addParent(component.targetField, { field: p, type: 'formula' }, dependencyMap);
          });
        }
      }
    }
  },
  'Notification': {
    addFunc: (component, add: Add) => { },
    getSlugs: (component) => { return { slugs: [] } },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      // TODO(Riley): using targetPrefix is one way we can do this, another would be to remove the slugs and consider this to have 2-3 "main" target fields:
      // targetPrefix + '_sms', targetPrefix + '_email', and possibly targetPrefix + '_whatsapp'
      // The second option is more true, but complicates the rest of the logic
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetPrefix, dependencyMap });
      if (typeof component.recipient === 'object') {
        addParent(component.targetPrefix, { type: 'formula', field: component.recipient.phoneField }, dependencyMap);
        addParent(component.targetPrefix, { type: 'formula', field: component.recipient.emailField }, dependencyMap);
      }
      // TODO(Riley): should this be formula or conditional? (or a third thing?) - make sure it aligns with InlineNotification
      const enabledQueryParents = component.initial_notification.enabled_when.kind === 'SQL'
        // ? await getAllFormulaParents(component.formula)
        // : await getAllFormulaParents(CompileExpressionToJS(component.formula));
        // TODO(Riley): this is raw SQL, not JS, so we need to parse it using a SQL parser.
        ? [] // safeGetFormulaParents(component.initial_notification.enabled_when.sql)
        : safeGetFormulaParents(CompileExpressionToJS(component.initial_notification.enabled_when.expr));
      (new Set(enabledQueryParents)).forEach((p) => {
        addParent(component.targetPrefix, { field: p, type: 'formula' }, dependencyMap);
      });

      (component.initial_notification.subsurveys ?? []).forEach(subsurveyLink => {
        if (typeof subsurveyLink.name === 'object' && 'targetField' in subsurveyLink.name) {
          addParent(component.targetPrefix, { field: subsurveyLink.name.targetField, type: 'formula' }, dependencyMap);
        }
      });

      if (component.initial_notification.content_template) {
        (component.initial_notification.content_template.variables.forEach(variable => {
          addParent(component.targetPrefix, { field: variable.variable, type: 'content' }, dependencyMap);
        }));
      }

      (component.followups ?? []).forEach(followup => {

        // TODO(Riley): should this be formula or conditional? (or a third thing?) - make sure it aligns with InlineNotification
        const enabledFollowupParents = followup.send_if.kind === 'SQL'
          // ? await getAllFormulaParents(followup.send_if.expr)
          // : await getAllFormulaParents(CompileExpressionToJS(followup.send_if.expr));
          // TODO(Riley): this is raw SQL, not JS, so we need to parse it using a SQL parser.
          ? [] // safeGetFormulaParents(followup.send_if.sql)
          : safeGetFormulaParents(CompileExpressionToJS(followup.send_if.expr));

        (new Set(enabledFollowupParents)).forEach((p) => {
          addParent(component.targetPrefix, { field: p, type: 'formula' }, dependencyMap);
        });

        (followup.subsurveys ?? []).forEach(subsurveyLink => {
          if (typeof subsurveyLink.name === 'object' && 'targetField' in subsurveyLink.name) {
            addParent(component.targetPrefix, { field: subsurveyLink.name.targetField, type: 'formula' }, dependencyMap);
          }
        });

        if (followup.content_template) {
          (followup.content_template.variables.forEach(variable => {
            addParent(component.targetPrefix, { field: variable.variable, type: 'content' }, dependencyMap);
          }));
        }
      });
    },
  },
  'Templated Block': {
    addFunc: (component, add: Add) => { },
    getSlugs: (component) => { return { slugs: [] } },
    getRequirements: (component) => [],
    // TODO(Riley)
    calculateDependencies: (component, dependencyMap: DependencyMap) => { },
  },
  'Conditional Block': {
    addFunc: (component, add: Add) => { },
    getSlugs: (component) => { return { slugs: [] } },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      const virtualNode: DependencyNode = {
        id: `_${slugid.nice()}`, // Assign a UID so we don't conflate with other conditional blocks
        parents: [],
        children: [],
        component,
      }
      dependencyMap.set(virtualNode.id, virtualNode);
      connectConditionsToTopLevelComponents(component.conditionalOn, component.components, dependencyMap, virtualNode);
    },
  },
  'Action': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Action',
          ...(typeof component.action !== 'string' && component.action.kind === 'Visit External Link' ? { 'Target Field': component.action.targetField } : {}),
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify({
            id: component.id,
            action: component.action,
            confirmation: component.confirmation as v0.Text,
            toast: component.toast as v0.Text,
            explanation: component.explanation as v0.RichText,
          }),
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: (component.fields || []) as v0.StandaloneSlug[] };
    },
    getRequirements: (component) => [],
    calculateDependencies: (
      component,
      dependencyMap: DependencyMap
    ) => {
      // TODO(Riley): implement
    }
  },
  'Address': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Address',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted', 'Separate Fields'],
          Metadata: JSON.stringify({ lookups: component.lookups }),
        }
      })
    },
    getSlugs: (component) => {
      const slugs: Array<v0.StandaloneSlug | v0.SuffixSlug> = [
        '_county',
        '_tract',
        '_lat',
        '_lng',
        '_building',
        '_subpremise',
        '_street',
        '_city',
        '_state',
        '_zip',
        '_plus_4',
        '_dpb',
        '_formatted',
        '_validation',
      ] as v0.SuffixSlug[];

      for (const lookup of (component.lookups ?? [])) {
        slugs.push('_' + lookup as v0.SuffixSlug);
      }
      return { slugs };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    },
  },
  'Collapsible Content': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id + "top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Collapsible Content',
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify(component)
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { },
  },
  'Alert': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      const buttonAction = component.button?.action;
      const visitLinkTargetField = buttonAction &&
        typeof buttonAction !== 'string' &&
        buttonAction.kind === 'Visit External Link'
        ? buttonAction.targetField
        : undefined;
      // blockProgress works by assigning a UUID-derived Target Field that nothing ever writes to.
      // The section completion system sees an unanswered non-optional field and treats the section as
      // incomplete, blocking progress. The block lifts when conditionalOn hides the Alert.
      const blockField = component.blockProgress ? id.replace(/-/g, '_') : undefined;
      add.Question({
        id: generateUuid(id + "top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Alert',
          'English Content': '--',
          'Spanish Content': '--',
          // blockField takes priority: if blockProgress is set, it must be the Target Field so that
          // nothing ever writes to it and the block only lifts when conditionalOn hides the Alert.
          // visitLinkTargetField is only used as Target Field when blockProgress is not set.
          ...((blockField ?? visitLinkTargetField) ? { 'Target Field': blockField ?? visitLinkTargetField } : {}),
          ...(visitLinkTargetField ? { 'Who Can Edit': [add.Role('Screener', ['Screener'])] } : {}),
          Metadata: JSON.stringify(component)
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: (component.button?.fields || []) as v0.StandaloneSlug[] };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { },
  },
  'ApplicantCreator': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': component.kind,
          'Target Field': component.targetField,
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          Translation: [add.Translation(component.buttonText as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify(component),
        }
      })
    },
    getSlugs: (component) => {
      const slugs: Array<v0.StandaloneSlug | v0.SuffixSlug> = [] as v0.StandaloneSlug[];
      for (const otherField of component.newApplicantInitialInfo.otherFields || []) {
        slugs.push(otherField.destinationField as v0.StandaloneSlug);
      }
      slugs.push(component.newApplicantInitialInfo.existingApplicantReferencePrefix + '_id' as v0.StandaloneSlug);
      slugs.push(component.newApplicantInitialInfo.existingApplicantReferencePrefix + '_name' as v0.StandaloneSlug);

      return { slugs };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      addParent(component.targetField, { type: 'formula', field: component.newApplicantInitialInfo.nameSourceField }, dependencyMap);
      // TODO(Riley): not sure if we want to mark the destinationFields as dependents. Could do weird things (loops), and isn't true for a single participant
      (component.newApplicantInitialInfo.otherFields ?? []).forEach(source => {
        findFields(source.source).forEach(sourceField => {
          addParent(component.targetField, { type: 'formula', field: sourceField }, dependencyMap);
        })
      });
    },
  },
  'ArcGIS Attachment Viewer': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'ArcGIS Attachment Viewer',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify(component),
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      if (typeof component.objectIds === 'object') {
        addParent(component.targetField, { type: 'formula', field: component.objectIds.field }, dependencyMap);
      }
    },
  },
  'Assignee': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Assignee',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    },
  },
  'Attachment': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Attachment',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': [
            'Formatted',
            ...(component.detectFaces ? ['Detect Faces'] : []),
            ...(component.extractText ? ['Extract Text'] : []),
            ...(component.directUploadOnly ? ['Direct Upload Only'] : [])
          ],
          Metadata: JSON.stringify({
            limitOne: component.limitOne,
            realtimeVerifications: component.realtimeVerifications,
            allowSendingLinkToUpload: component.allowSendingLinkToUpload,
            enableCamera: component.enableCamera,
            uploadMode: component.uploadMode,
            humanReadableName: component.humanReadableName,
            dragAndDrop: component.dragAndDrop,
            assistance: component.assistance
          }),
        }
      })
    },
    getSlugs: (component) => {
      const slugs = [] as (v0.SuffixSlug | v0.StandaloneSlug)[];
      if (component.realtimeVerifications?.length) {
        slugs.push(...['_extracted_fields', '_verification_results', '_video', '_metadata', '_queue_id', '_analysis_result_id'] as v0.SuffixSlug[]);
      }
      if (component.detectFaces) {
        slugs.push(...[
          '_face',
          '_duplicates',
          '_related',
          '_unindexed_reason',
          '_conversion'] as v0.SuffixSlug[]);
      }
      if (component.extractText) {
        slugs.push('_text' as v0.SuffixSlug);
        slugs.push('_text_array' as v0.SuffixSlug);
        slugs.push('_text_error' as v0.SuffixSlug);
      }
      if (component.assistance) {
        // target field slug that allow tracking what went into producing the target field's value
        // aka the conversation id.
        slugs.push(
          ...flattenAnalyses(component.assistance.analyses)
            .flatMap(a => [a.targetField, `${a.targetField}_analysis_result_id`, `${a.targetField}_jobs`, `${a.targetField}_error`] as v0.StandaloneSlug[])
        );
      }
      return { slugs };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });

      (component.realtimeVerifications || []).forEach(verification => {
        if (verification.textPresent && ('anyOf' in verification.textPresent || 'allOf' in verification.textPresent)) {
          ((verification.textPresent?.allOf ?? []).concat(verification.textPresent?.anyOf ?? [])).forEach(stringOrField => {
            if (typeof stringOrField === 'object') {
              addParent(component.targetField, { field: stringOrField.field, type: 'formula' }, dependencyMap);
            }
          })
        }
      });
    }
  },
  'BankRoutingNumber': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Bank Routing Number',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: ['_bankname' as v0.SuffixSlug] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    },
  },
  'Benefits Calculator': {
    addFunc: (component, add: Add) => {
      add.Question({
        id: generateUuid(component),
        fields: {
          'Field Type': 'Benefits Calculator',
          'Target Field': component.targetField,
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          modern: true,
          definition: component,
          Metadata: JSON.stringify({
            flattenIntoFieldsWithPrefix: component.flattenIntoFieldsWithPrefix,
          }),
        }
      } as any);
    },
    getSlugs: (component) => {
      let slugs: Array<v0.StandaloneSlug | v0.SuffixSlug> = [];
      if (component.flattenIntoFieldsWithPrefix) {
        slugs = slugs.concat([
          '_pre_foodstamps_Basic_Food_Program_eligible',
          '_pre_foodstamps_Basic_Food_Program_amount',
          '_pre_foodstamps_CalFresh_eligible',
          '_pre_foodstamps_CalFresh_amount',
          '_pre_wic_WIC_eligible',
          '_pre_wic_WIC_amount',
          '_pre_welfare_WorkFirst_eligible',
          '_pre_welfare_WorkFirst_amount',
          '_pre_welfare_CalWorks_eligible',
          '_pre_welfare_CalWorks_amount',
          '_pre_welfare_General_Relief_eligible',
          '_pre_welfare_General_Relief_amount',
          '_pre_healthcare',
          '_pre_childcare_Child_Care_Assistance_eligible',
          '_pre_childcare_Child_Care_Assistance_amount',
          '_pre_utilities_HEAP_eligible',
          '_pre_utilities_HEAP_amount',
          '_pre_utilities_Lifeline_eligible',
          '_pre_utilities_Lifeline_amount',
          '_pre_utilities_FERA-CARE_eligible',
          '_pre_utilities_FERA-CARE_amount',
          '_pre_utilities_CARE_eligible',
          '_pre_utilities_CARE_amount',
          '_pre_taxes',
          '_pre_eitc_eitc_eligible',
          '_pre_eitc_eitc_amount',
          '_pre_cctc_Child_Care_Tax_Credit_eligible',
          '_pre_cctc_Child_Care_Tax_Credit_amount',
          '_pre_ctc_Child_Tax_Credit_eligible',
          '_pre_ctc_Child_Tax_Credit_amount',
          '_pre_hsi_hsi_eligible',
          '_pre_hsi_hsi_amount',
          '_pre_hsi_Not_Eligible_eligible',
          '_pre_hsi_Not_Eligible_amount',
          '_pre_hsi__eligible',
          '_pre_hsi__amount',
          '_pre_hsi_Affordable_Connectivity_Program_eligible',
          '_pre_hsi_Affordable_Connectivity_Program_amount',
          '_pre_sdoh',
          '_pre_unemployment_Unemployment_Insurance_eligible',
          '_pre_unemployment_Unemployment_Insurance_amount',
          '_pre_ssissd_ssissd_eligible',
          '_pre_ssissd_ssissd_amount',
          '_pre_section8',
          '_post_foodstamps_Basic_Food_Program_eligible',
          '_post_foodstamps_Basic_Food_Program_amount',
          '_post_foodstamps_CalFresh_Program_eligible',
          '_post_foodstamps_CalFresh_Program_amount',
          '_post_wic_WIC_eligible',
          '_post_wic_WIC_amount',
          '_post_welfare_WorkFirst_eligible',
          '_post_welfare_WorkFirst_amount',
          '_post_welfare_CalWorks_eligible',
          '_post_welfare_CalWorks_amount',
          '_post_welfare_General_Relief_eligible',
          '_post_welfare_General_Relief_amount',
          '_post_healthcare',
          '_post_childcare_Child_Care_Assistance_eligible',
          '_post_childcare_Child_Care_Assistance_amount',
          '_post_utilities_HEAP_eligible',
          '_post_utilities_HEAP_amount',
          '_post_utilities_Lifeline_eligible',
          '_post_utilities_Lifeline_amount',
          '_post_utilities_FERA-CARE_eligible',
          '_post_utilities_FERA-CARE_amount',
          '_post_utilities_CARE_eligible',
          '_post_utilities_CARE_amount',
          '_post_taxes',
          '_post_eitc_eitc_eligible',
          '_post_eitc_eitc_amount',
          '_post_cctc_Child_Care_Tax_Credit_eligible',
          '_post_cctc_Child_Care_Tax_Credit_amount',
          '_post_ctc_Child_Tax_Credit_eligible',
          '_post_ctc_Child_Tax_Credit_amount',
          '_post_hsi_hsi_eligible',
          '_post_hsi_hsi_amount',
          '_post_hsi_Not_Eligible_eligible',
          '_post_hsi_Not_Eligible_amount',
          '_post_hsi__eligible',
          '_post_hsi__amount',
          '_post_hsi_Affordable_Connectivity_Program_eligible',
          '_post_hsi_Affordable_Connectivity_Program_amount',
          '_post_sdoh',
          '_post_unemployment_Unemployment_Insurance_eligible',
          '_post_unemployment_Unemployment_Insurance_amount',
          '_post_ssissd_ssissd_eligible',
          '_post_ssissd_ssissd_amount',
          '_post_section8',
        ].map((fieldSuffix) => (component.flattenIntoFieldsWithPrefix + fieldSuffix) as v0.StandaloneSlug));
      }

      return { slugs }
    },
    getRequirements: (component) => {
      if (component.targetField) {
        return [{ kind: 'Exists', field: component.targetField }];
      }
      return [];
    },
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      if (!component.targetField) {
        return;
      }

      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });

      if (component.household_calc_target_field) {
        addParent(component.targetField, { type: 'formula', field: component.household_calc_target_field }, dependencyMap);
      }

      // Find and add all fields referenced in ValueExpr in required_info
      const requiredValueExprKeys: KeysMatching<Required<v0.BenefitsCalculator['required_info']>, v0.ValueExpr>[] = [
        'monthly_income',
        'live_zip',
        'gi_income',
      ];
      requiredValueExprKeys.forEach(valueExprKey => {
        findFields(component.required_info[valueExprKey]).forEach(valueExprField => {
          addParent(component.targetField!, { type: 'formula', field: valueExprField }, dependencyMap);
        });
      })

      // Find and add all fields referenced in ValueExpr in optional_info.snap
      if (component.optional_info?.snap) {
        const snapValueExprKeys: KeysMatching<Required<Required<v0.BenefitsCalculator['optional_info']>['snap']>, v0.ValueExpr>[] = [
          'child_care_cost',
          'feed_family_cost',
          'homeless',
          'housing_costs',
          'ssi_children_number',
          'ssdi_amount',
          'ssi_spouse_amount',
          'ssi_you_amount',
          'ssi_income_kids',
          'welfare_amount',
        ];
        snapValueExprKeys.forEach(valueExprKey => {
          findFields((component.optional_info.snap![valueExprKey])).forEach(valueExprField => {
            addParent(component.targetField!, { type: 'formula', field: valueExprField }, dependencyMap);
          })
        });
      }

      // Find and add all fields referenced in ValueExpr in optional_info.ctc
      if (component.optional_info?.ctc) {
        const ctcValueExprKeys: KeysMatching<Required<Required<v0.BenefitsCalculator['optional_info']>['ctc']>, v0.ValueExpr>[] = [
          'married',
        ];
        ctcValueExprKeys.forEach(valueExprKey => {
          findFields((component.optional_info.ctc![valueExprKey])).forEach(valueExprField => {
            addParent(component.targetField!, { type: 'formula', field: valueExprField }, dependencyMap);
          })
        });
      }

      // Find and add all fields referenced in ValueExpr in optional_info.otherInfo
      if (component.optional_info?.otherInfo) {
        const otherValueExprKeys: KeysMatching<Required<Required<v0.BenefitsCalculator['optional_info']>['otherInfo']>, v0.ValueExpr>[] = [
          'food_stamps',
        ];
        otherValueExprKeys.forEach(valueExprKey => {
          if (component.optional_info.otherInfo![valueExprKey]) {
            findFields((component.optional_info.otherInfo![valueExprKey])!).forEach(valueExprField => {
              addParent(component.targetField!, { type: 'formula', field: valueExprField }, dependencyMap);
            })
          }
        });
      }

      if (component.hideImpactIf) {
        findFields((component.hideImpactIf)!).forEach(boolExprField => {
          addParent(component.targetField!, { type: 'formula', field: boolExprField }, dependencyMap);
        })
      }
    },
  },
  'Card Management': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Card Management',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField || 'card_id',
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': [
            ...(component.viewOnly ? ['View Only'] : [])
          ],
          Metadata: JSON.stringify(component),
        }
      })
      const extraFields = ['legal_name', 'birth_date', 'home_address', 'email', 'phone'];
      for (const field of extraFields) {
        add.Question({
          id: generateUuid(field+id),
          createdTime: '',
          fields: {
            Question: id,
            'Field Type': 'Single Line Text Entry',
            'Who Can Edit': [add.Role('Screener', ['Screener'])],
            'Target Field': field,
            'English Content': '--',
            'Spanish Content': '--',
            'Additional Options': ['Hidden']
          }
        })
      }
    },
    getSlugs: (component) => {
      const slugs: Array<v0.StandaloneSlug | v0.SuffixSlug> = [];
      const suffixes = ['_id', '_serial', '_last4', '_id_setup_link'];
      if (component.targetField) {
        slugs.push(...suffixes as v0.SuffixSlug[])
      } else {
        for (const suffix of suffixes) {
          slugs.push('card' + suffix as v0.StandaloneSlug);
        }
      }
      return { slugs };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });

      if (typeof component.cardType === 'object' && component.cardType.kind === 'USBank') {
        const usBankCardKeys: KeysMatching<Required<v0.USBankCard>, v0.TargetFieldRef>[] = ['addressField', 'emailField', 'ssnField', 'dobField'];
        for (const prop of usBankCardKeys) {
          if (component.cardType[prop]) {
            addParent(component.targetField, { type: 'formula', field: component.cardType[prop]! }, dependencyMap);
          }
        }
      }
    },
  },
  'Collection': {
    addFunc: (component, add: Add) => {
      for (const c of component.components) {
        if (c.kind === 'Section') {
          add.Section(c, add.Depth + 1);
          continue;
        }
        const t = TRANSLATORS[c.kind as keyof typeof TRANSLATORS];
        if (t) t.addFunc(c as any, add);
      }
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      component.components.forEach(((innerComponent: v0.CollectionComponent) => {
        TRANSLATORS[innerComponent.kind].calculateDependencies(innerComponent as any, dependencyMap);
      }));
    }
  },
  'Computed': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      
      let formula = typeof component.formula === 'string' 
        ? component.formula 
        : CompileExpressionToJS(component.formula as any);
      if (component.format === 'number') {
        formula = "parseFloat( " + formula + " )"
      }

      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Computed',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
          Metadata: JSON.stringify({
            formula: formula,
            format: component.format,
          }),
        }
      })
    },
    getSlugs: (component) => {
      return {
        slugs: ['_error' as v0.SuffixSlug]
      };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      const parents = typeof component.formula == 'string'
        // ? await getAllFormulaParents(component.formula)
        // : await getAllFormulaParents(CompileExpressionToJS(component.formula));
        ? safeGetFormulaParents(component.formula)
        : safeGetFormulaParents(CompileExpressionToJS(component.formula));

      (new Set(parents)).forEach((p) => {
        addParent(component.targetField, { field: p, type: 'formula' }, dependencyMap);
      });
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    }
  },
  'Confirmation': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Confirmation',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
          ...(component.value ? { 'Value': component.value } : {}),
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    }
  },
  'Consent': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Consent',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': '_consent',
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify(component),
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  },
  'ContactConfirmation': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Contact Confirmation',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          // Don't Resume by default
          'Additional Options': ['Formatted', ...[component.resume ? '' : 'Don\'t Resume']],
          Metadata: JSON.stringify({
            contact_field: component.contactField,
            ...filterDict(component, (k, v) => k !== 'content' && k !== 'targetField' && k !== 'resume')
          }),
        }
      })
    },
    getSlugs: (component) => {
      // Trailing underscore used to avoid a collision with field names
      return { slugs: [(component.contactField || 'phone_number') + '_confirmed_info_' as v0.StandaloneSlug] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    }
  },
  'Date': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Date',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
          Metadata: JSON.stringify({
            monthAndYearOnly: component.monthAndYearOnly,
          }),
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    }
  },
  'Deck Verification': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Deck Verification',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
          Metadata: JSON.stringify(component),
        }
      })
    },
    getSlugs: (component) => {
      return {
        slugs: [
          "_job_guid" as v0.StandaloneSlug,
          "_data" as v0.StandaloneSlug,
        ]
      };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    }
  },
  'Argyle Income Verification': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Argyle Income Verification',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
          Metadata: JSON.stringify(component),
        }
      })
    },
    getSlugs: (component) => {
      return {
        // _tax_document_text: OCR output only (Argyle ocr_data as JSON), not the document file
        slugs: [
          "_user_id" as v0.SuffixSlug,
          "_paystub_data" as v0.SuffixSlug,
          "_identity_data" as v0.SuffixSlug,
          "_tax_document_text" as v0.SuffixSlug,
          "_error" as v0.SuffixSlug,
        ]
      };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    }
  },
  'Document Summary': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Document Summary',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
          Metadata: JSON.stringify(component),
        }
      })
    },
    getSlugs: (component) => {
      return {
        slugs: ["_acks" as v0.SuffixSlug]
      };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    }
  },
  'Duplicate Review': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Duplicate Review',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          ...(component.content ? { Translation: [add.Translation(component.content as v0.Text)] } : {}),
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify(component),
        }
      })
    },
    getSlugs: (component) => {
      return {
        slugs: [
          ...(component.customDecisions?.map(c => (c as any).sharedKeyField || (c as any).individualDecisionField || '').filter(f => f) || [])
        ]
      };
    },
    getRequirements: (component) => [{
      kind: 'Or',
      clauses: [{
        kind: 'Not',
        clause: {
          kind: 'Exists',
          field: component.flagField
        }
      }, {
        kind: 'Exists',
        field: component.targetField
      }]
    }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      addParent(component.targetField, { type: 'formula', field: component.flagField }, dependencyMap);
      addParent(component.targetField, { type: 'formula', field: component.tiebreakerField.field }, dependencyMap);
      component.comparisonFields.forEach(comparisonField => {
        addParent(component.targetField, { type: 'formula', field: comparisonField }, dependencyMap);
      });
      // CustomDecisions handled by slugs
    }
  },
  'Encrypted Value Verification': {
    addFunc: (component, add: Add) => {
      add.Question({
        id: generateUuid(component),
        createdTime: '',
        fields: {
          'Field Type': 'Encrypted Value Verification',
          Translation: [add.Translation(component.content)],
          'Target Field': component.targetField,
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Metadata': JSON.stringify({
            sourceField: component.sourceField,
            lastFourOnly: component.lastFourOnly,
          }),
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      addParent(component.targetField, { type: 'formula', field: component.sourceField }, dependencyMap);
    }
  },
  'Explanation': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);      
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Explanation',
          Translation: [add.Translation(Array.isArray(component.content) ? { 'en': '' } : component.content)],
          'English Content': '--',
          'Spanish Content': '--',
          ...(Array.isArray(component.content) ? {
            'Metadata': JSON.stringify({
              modernType: component,
            })
          } : {}),
          'Additional Options': [
            'Formatted',
            ...(component.collapsible ? ['Collapsible'] : []),
            ...(component.forceFullWidth ? ['Force Full Width'] : []),
          ],
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  },
  'Fillable Form': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Fillable Form',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          'English Content': '--',
          Metadata: JSON.stringify({ ...component, instructions: w9.instructions } satisfies (v0.FillableForm & v0.CommonProperties))
        }
      })
    },
    getSlugs: (component) => {
      return {
        slugs: [
          component.form.signatureStorageField as v0.StandaloneSlug,
          '_irs_flag' as v0.SuffixSlug
        ]
      };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      addParent(component.targetField, { type: 'formula', field: component.form.mailingAddressField }, dependencyMap);
      addParent(component.targetField, { type: 'formula', field: component.form.tinField }, dependencyMap);
      if (component.form.businessInfo) {
        const businessInfoKeys: KeysMatching<Required<v0.FillableForm['form']>['businessInfo'], v0.TargetFieldRef>[] = [
          'einField',
          'businessNameField',
          'businessTypeField',
          'otherBusinessTypeField',
          'box3bField',
        ];
        for (const key of businessInfoKeys) {
          addParent(component.targetField, { type: 'formula', field: component.form.businessInfo[key] }, dependencyMap);
        }
      }
    }
  },
  'Flag': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);

      type FlagMetadata = {
        unique?: {
          [program: string]: string[]
        },
        fuzzy_unique?: {
          [program: string]: string[]
        },
        required_info?: {
          [program: string]: { [key: string]: string }
        },
        'max_count'?: number,
        'max_sum'?: number,
        'sum_target'?: string,
        'async'?: boolean,
        query?: {
          [deployment: string]: {
            'params': string[],
            'sql': string,
            'depends_on': string[]
          }
        }
      }

      let metadata: FlagMetadata = {
        ...(component.flag.maxCount ? { max_count: component.flag.maxCount } : {}),
      };

      if (component.flag.kind === 'Unique') {
        let unique: FlagMetadata['unique'] = {};
        let fuzzy: FlagMetadata['fuzzy_unique'] = {};
        let requiredInfo: FlagMetadata['required_info'] = {};
        for (const programDef of component.flag.programs) {
          let thisProgram = programDef.program;
          let exactFields = programDef.fields.filter(f => f.mode === 'exact').map(f => f.field);
          let fuzzyFields = programDef.fields.filter(f => f.mode === 'fuzzy').map(f => f.field);
          if (exactFields.length) unique[thisProgram] = exactFields;
          if (fuzzyFields.length) fuzzy[thisProgram] = fuzzyFields;
          let requiredKeys = (programDef.requiredInfo || []).filter(f => f.key);
          if (requiredKeys.length) requiredInfo[thisProgram] = requiredKeys.reduce((acc, cur) => {
            acc[cur.key] = cur.value || '';
            return acc;
          }, {} as Record<string, string>);
        }
        if (Object.keys(unique).length) metadata.unique = unique;
        if (Object.keys(fuzzy).length) metadata.fuzzy_unique = fuzzy;
        if (Object.keys(requiredInfo).length) metadata.required_info = requiredInfo;
      }

      if (component.flag.kind === 'Query Flag') {
        if (!metadata.query) metadata.query = {};
        for (const programDef of component.flag.programs) {
          metadata.query[programDef.program] = {
            sql: programDef.query.sql,
            params: programDef.params || [],
            depends_on: programDef.depends_on || [],
          };
          if (programDef.requiredInfo) {
            if (!metadata.required_info) metadata.required_info = {};
            metadata.required_info[programDef.program] = programDef.requiredInfo.reduce((acc, cur) => {
              acc[cur.key] = cur.value || '';
              return acc;
            }, {} as Record<string, string>);
          }
        }
      }

      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Flag',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify({
            ...metadata,
            async: component.async
          } as FlagMetadata),
          Translation: [add.Translation(component.content)],
          'Additional Options': [...(component.advisory ? ['Advisory'] : [])]
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: ['_comment' as v0.SuffixSlug, component.targetField as v0.StandaloneSlug] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      // For dependencies, we only consider those fields that are in the program in which the flag is defined.
      if (component.flag.kind === 'Query Flag') {
        const programDef = component.flag.programs.find(p => p.program === '.');
        if (programDef) {
          programDef.params?.forEach(param => {
            addParent(component.targetField, { type: 'formula', field: param }, dependencyMap);
          });
          programDef.depends_on?.forEach(dep => {
            addParent(component.targetField, { type: 'formula', field: dep }, dependencyMap);
          });
          // Note: We are omitting requiredInfo from the dependencies because it is not a dependency!
          // It is target fields on another app, so updating it should not cause RS to recompute a flag.
        }
      } else if (component.flag.kind === 'Unique') {
        const programDef = component.flag.programs.find(p => p.program === '.');
        if (programDef) {
          programDef.fields.forEach(field => {
            addParent(component.targetField, { type: 'formula', field: field.field }, dependencyMap);
          });
          // Note: We are omitting requiredInfo from the dependencies because it is not a dependency!
          // It is target fields on another app, so updating it should not cause RS to recompute a flag.
        }
      }
    }
  },
  'GiveCard Intake': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'GiveCard Intake',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify(component),
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: ['_recipient_details' as v0.SuffixSlug] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
    },
  },
  'Household Calculator': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Household Calculation',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
          Metadata: JSON.stringify({
            roles: component.roles,
            title: component.title,
            memberCountLabel: component.memberCountLabel,
            showYou: component.showYou,
            referenceDate: component.referenceDate,
            lockHHCalculator: component.lockHHCalculator,
          })
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: ['_count' as v0.SuffixSlug] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      if (component.showYou) {
        addParent(component.targetField, { type: 'formula', field: component.showYou.nameField }, dependencyMap);
        if (component.showYou.birthdateField) {
          addParent(component.targetField, { type: 'formula', field: component.showYou.birthdateField }, dependencyMap);
        }
      }

      if (component.referenceDate && typeof component.referenceDate === 'string') {
        addParent(component.targetField, { type: 'formula', field: component.referenceDate }, dependencyMap);
      }

      component.roles.forEach(role => {
        if (typeof role.dataCollectOptions.maxCount === 'object' && 'field' in role.dataCollectOptions.maxCount) {
          addParent(component.targetField, { type: 'formula', field: role.dataCollectOptions.maxCount.field }, dependencyMap);
        }
      });
    }
  },
  'Income Calculator': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Income Calculation',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
          Metadata: JSON.stringify(component)
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: ['_details' as v0.SuffixSlug] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    }
  },
  'Inline Language Selector': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          Translation: [add.Translation(component.content)],
          'Field Type': 'Inline Language Selector',
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  },
  'InlineNotification': {
    addFunc: (component, add: Add) => {

      let contentIsArray = {
        'message': Array.isArray(component.initial_notification.message),
        'email_message': Array.isArray(component.initial_notification.email_message)
      };

      if (component.contactMethod !== 'email') {
        add.Question({
          id: generateUuid(component.name + ' - SMS'),
          createdTime: '',
          fields: {
            Question: component.name + ' - SMS', // This doesn't appear to do anything...
            'Field Type': 'Notification',
            'Who Can Edit': [add.Role('Screener', ['Screener'])],
            'Target Field': component.targetPrefix + '_sms',
            'Translation': [add.Translation(component.initial_notification.message)],
            'English Content': '--',
            'Spanish Content': '--',
            'Additional Options': ['Formatted', 'Optional'],
            Metadata: JSON.stringify({
              links: (component.initial_notification.subsurveys || []).reduce((o, s) => {
                o[s.variable] = s.name;
                return o;
              }, {} as Record<string, string>),
              contact_method: component.contactMethod,
              notify_applicant_phone: true,
              service_sid: component.smsService,
              timezone: component.timezone,
              notify_applicant_phone_key: (component.recipient as v0.CustomContact)?.phoneField,
              notify_applicant_email_key: undefined,
              ...(contentIsArray['message']
                ? { messageBlocks: component.initial_notification.message }
                : {}),
              sql: component.initial_notification.enabled_when.kind === 'SQL'
                ? component.initial_notification.enabled_when.sql
                : CompileExpressionToSQL({
                  cond: {
                    kind: 'And',
                    clauses: [
                      {
                        field: (component.recipient as v0.CustomContact)?.phoneField || 'phone_number',
                        kind: 'Exists'
                      },
                      component.initial_notification.enabled_when.expr
                    ]
                  },
                  orderBy: component.initial_notification.enabled_when.orderBy
                }),
              js: component.initial_notification.enabled_when.kind === 'Click'
                ? CompileExpressionToJS(component.initial_notification.enabled_when.expr)
                : '',
              name: component.name + ' - SMS',
              inline: true,
              allowedUserTags: component.allowedUserTags,
            }),
          }
        });
      }

      if (component.contactMethod !== 'sms') {
        add.Question({
          id: generateUuid(component),
          createdTime: '',
          fields: {
            Question: component.name + ' - Email', // This doesn't appear to do anything...
            'Field Type': 'Notification',
            'Who Can Edit': [add.Role('Screener', ['Screener'])],
            'Target Field': component.targetPrefix + '_email',
            'Translation': [add.Translation(component.initial_notification.email_message || component.initial_notification.message)],
            'English Content': '--',
            'Spanish Content': '--',
            'Additional Options': ['Formatted', 'Optional'],
            Metadata: JSON.stringify({
              links: (component.initial_notification.subsurveys || []).reduce((o, s) => {
                o[s.variable] = s.name;
                return o;
              }, {} as Record<string, string>),
              contact_method: component.contactMethod,
              notify_applicant_email: true,
              subject: component.initial_notification.email_subject,
              from: component.emailSender,
              notify_applicant_phone_key: undefined,
              notify_applicant_email_key: (component.recipient as v0.CustomContact)?.emailField,
              timezone: component.timezone,
              ...(contentIsArray['email_message']
                ? { messageBlocks: component.initial_notification.email_message }
                : contentIsArray['message']
                  ? { messageBlocks: component.initial_notification.message }
                  : {}),
              sql: component.initial_notification.enabled_when.kind === 'SQL'
                ? component.initial_notification.enabled_when.sql
                : CompileExpressionToSQL({
                  cond: {
                    kind: 'And',
                    clauses: [
                      {
                        field: (component.recipient as v0.CustomContact)?.emailField || 'email',
                        kind: 'Exists'
                      },
                      component.initial_notification.enabled_when.expr
                    ]
                  },
                  orderBy: component.initial_notification.enabled_when.orderBy
                }),
              js: component.initial_notification.enabled_when.kind === 'Click'
                ? CompileExpressionToJS(component.initial_notification.enabled_when.expr)
                : '',
              name: component.name + ' - Email',
              inline: true,
              allowedUserTags: component.allowedUserTags,
            }),
          }
        });
      }
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [{
      kind: 'Or',
      clauses: [{
        kind: 'Exists',
        field: component.targetPrefix + '_sms'
      }, {
        kind: 'Exists',
        field: component.targetPrefix + '_email'
      }]
    }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetPrefix + '_sms', dependencyMap });
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetPrefix + '_email', dependencyMap });
      // TODO(Riley): should this be formula or conditional? (or a third thing?)
      const parents = component.initial_notification.enabled_when.kind === 'SQL'
        // ? await getAllFormulaParents(component.formula)
        // : await getAllFormulaParents(CompileExpressionToJS(component.formula));
        // TODO(Riley): this is raw SQL, not JS, so we need to parse it using a SQL parser.
        ? [] // safeGetFormulaParents(component.initial_notification.enabled_when.sql)
        : safeGetFormulaParents(CompileExpressionToJS(component.initial_notification.enabled_when.expr));
      (new Set(parents)).forEach((p) => {
        addParent(component.targetPrefix + '_sms', { field: p, type: 'formula' }, dependencyMap);
        addParent(component.targetPrefix + '_email', { field: p, type: 'formula' }, dependencyMap);
      });

      if (component.initial_notification.content_template) {
        component.initial_notification.content_template.variables.forEach((variable) => {
          addParent(component.targetPrefix + '_sms', { field: variable.variable, type: 'formula' }, dependencyMap);
          addParent(component.targetPrefix + '_email', { field: variable.variable, type: 'formula' }, dependencyMap);
        });
      }

      if (component.recipient) {
        addParent(component.targetPrefix + '_sms', { field: component.recipient.phoneField, type: 'formula' }, dependencyMap);
        addParent(component.targetPrefix + '_email', { field: component.recipient.emailField, type: 'formula' }, dependencyMap);
      }
    }
  },
  'InlineSignature': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Inline Signature',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          Translation: [add.Translation(component.content as v0.Content)],
          'English Content': '--',
          'Spanish Content': '--',
          'Metadata': JSON.stringify({
            'anonymous': component.anonymous,
            'signer_name': component.signerNameTargetField,
            ...(Array.isArray(component.content) ? {
              'modernType': component
            } : {}),
            'captureRawSignature': component.captureRawSignature,
            'confirmButton': component.confirmButton,
            'typedMustMatchName': component.typedMustMatchName
          }),
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: ['_typed' as v0.SuffixSlug] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      if (component.signerNameTargetField && typeof component.signerNameTargetField === 'string') {
        addParent(component.targetField, { field: component.signerNameTargetField, type: 'formula' }, dependencyMap);
      }
    }
  },
  'Likert': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id,
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Likert',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': Array.isArray(component.questions) ? 'foobar' : (component.questions.targetField + '_output'),
          Translation: [add.Translation(component.content as v0.Text)],
          'Options (if relevant)': component.choices.map(add.Choice),
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': [
            'Formatted',
            ...(component.showValueScale ? ['Show Value Scale'] : []),
            ...(component.showSmileyFaceScale ? ['Show Smiley Face Scale'] : []),
            ...(component.display === 'horizontal' ? ['Horizontal Display'] : []),
          ],
          Metadata: JSON.stringify(component)
        }
      });
      // Hack for saving multiple target fields
      for (const q of Array.isArray(component.questions) ? component.questions : []) {
        add.Question({
          id,
          createdTime: '',
          fields: {
            Question: id,
            'Field Type': 'Single Select',
            Translation: [add.Translation(q.label as v0.Text)],
            'Who Can Edit': [add.Role('Screener', ['Screener'])],
            'Target Field': q.targetField,
            'Options (if relevant)': component.choices.map(add.Choice),
            'English Content': '--',
            'Spanish Content': '--',
            'Additional Options': ['Formatted', 'Horizontal', 'Hidden'],
          }
        })
      }
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => {
      // "Regular" likert
      if (Array.isArray(component.questions)) {
        return [{
          kind: 'And',
          clauses: component.questions.map(q => ({
            kind: 'Exists',
            field: q.targetField
          }))
        }];
      } else {
        // LikertDataSource
        return [{ kind: 'Exists', field: component.questions.targetField + '_output' }];
      }
    },
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      if (Array.isArray(component.questions)) {
        // LikertQuestions
        component.questions.forEach(question => {
          addConditionsAndSlugsAndContentDeps({ component, mainTargetField: question.targetField, dependencyMap });
        });
      } else {
        // Likert from data source
        // TODO(Riley): make sure this is right
        addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.questions.answerField, dependencyMap });
        addParent(component.questions.answerField, { field: component.questions.targetField, type: 'formula' }, dependencyMap);
        addParent(component.questions.answerField, { field: component.questions.displayField, type: 'formula' }, dependencyMap);
      }
    }
  },
  'Liveness Detection': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);

      let livenessFieldMap = {
        [component.selfie.targetField]: {
          options: ["Detect Faces"]
        },
        ...(component.identification ? {
          [component.identification.front.targetField]: {
            options: ["Detect Faces", "Extract Text"]
          },
          [component.identification.back.targetField]: {
            options: ["Scan Barcode"]
          }
        } : {})
      }

      for (let field in livenessFieldMap) {
        add.Question({
          id: generateUuid(field+id+"top"),
          createdTime: '',
          fields: {
            Question: generateUuid(field+id),
            "Field Type": 'Attachment',
            'Who Can Edit': [add.Role('Screener', ['Screener'])],
            "Target Field": field,
            Translation: [add.Translation({ en: '--' })],
            'English Content': '--',
            'Spanish Content': '--',
            'Additional Options': [...livenessFieldMap[field].options, 'Hidden', 'Live Attachment'],
          }
        });
      }

      // Add the liveness question (technically is filled out AFTER the previous 3 have been answered)
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Liveness Detection',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': [],
          Metadata: JSON.stringify(component)
        }
      });
    },
    getSlugs: (component) => {
      const slugs: Array<v0.StandaloneSlug | v0.SuffixSlug> = [];
      const fields = component.identification ? [component.selfie.targetField, component.identification.front.targetField, component.identification.back.targetField] : [component.selfie.targetField];
      for (const field of fields) {
        slugs.push(
          (field + '_video') as v0.StandaloneSlug,
          (field + '_metadata') as v0.StandaloneSlug,
          (field + '_queue_id') as v0.StandaloneSlug,
        );
      }
      if (component.identification) {
        slugs.push(
          (component.identification.back.targetField + '_barcode_results') as v0.StandaloneSlug,
          (component.identification.back.targetField + '_barcode_results_error') as v0.StandaloneSlug,
          (component.identification.back.targetField + '_barcode_queue_id') as v0.StandaloneSlug,
        );
      }
      return {
        slugs
      };
    },
    getRequirements: (component) => [{
      kind: 'And',
      clauses: [...(component.identification ? [{
        kind: 'Exists',
        field: component.identification.front.targetField
      }, {
        kind: 'Exists',
        field: component.identification.back.targetField
      }] : []), {
        kind: 'Exists',
        field: component.selfie.targetField
      }, {
        kind: 'Exists',
        field: component.targetField
      }]
    }] as v0.BooleanExpr[],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      addChild(component.targetField, { field: component.selfie.targetField, type: 'formula' }, dependencyMap);
      if (component.identification) {
        addChild(component.targetField, { field: component.identification.front.targetField, type: 'formula' }, dependencyMap);
        addChild(component.targetField, { field: component.identification.back.targetField, type: 'formula' }, dependencyMap);
      }
      if (component.contactFields) {
        component.contactFields.forEach(contactField => {
          addParent(component.targetField, { field: contactField, type: 'formula' }, dependencyMap);
        })
      }
    }
  },
  'LoginWidget': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      if ('version' in component && component.version > 1) {
        add.Question({
          id: generateUuid(id+"top1"),
          createdTime: '',
          fields: {
            Question: id,
            'Field Type': 'Login Widget',
            Translation: [add.Translation(component.buttonText as v0.Text)],
            'English Content': 'Check your status',
            'Spanish Content': 'Consulta tu estado',
            Metadata: JSON.stringify(removeContent(component)),
          }
        })
      } else {
        add.Question({
          id: generateUuid(id+"top2"),
          createdTime: '',
          fields: {
            Question: id,
            'Field Type': 'Login Widget',
            'English Content': '--',
            'Spanish Content': '--',
            Metadata: JSON.stringify({
              page: component.page,
            })
          }
        })
      }
    },
    getSlugs: (component) => {
      return {
        slugs: ('saveContactInfo' in component && component.saveContactInfo)
          ? [component.saveContactInfo.contactInfoField] as v0.SuffixSlug[]
          : []
      };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  },
  'Lookup': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);

      // Convert sensitive info from an array of field+action to key: value dict
      const sensitive_info: Record<string, 'hide' | 'encrypt' | 'hash'> = {};
      if (component.lookup.kind === 'Dynamo') {
        for (const info of component.lookup.sensitiveInfo || []) {
          sensitive_info[info.field] = info.action;
        }
      }

      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Lookup',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          ...(component.content ? { Translation: [add.Translation(component.content as v0.Text)] } : {}),
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify({
            ...(component.lookup.kind === 'Dynamo' ? {
              kind: 'dynamo_hash',
              // convert queryFields which is a { fieldInDynamo, fieldInAidKit }[] to a string
              query: component.lookup.queryFields.map(q => `${q.fieldInDynamo}:${q.fieldInAidKit}`).join(','),
              config_path: component.lookup.configPath,
              sensitive_info,
              contact_confirmation: component.lookup.contactConfirmationField,
            } : { kind: component.lookup.key, query: component.lookup.queryField }),
            ...(component.lookup.kind === 'Geo' ? { geo: true, geoType: 'geoLookup' } : {}),
            ...(component.lookup.kind === 'GeoShape' ? { geo: true, geoType: 'geoShape' } : {}),
            ...(component.lookup.kind === 'GeoPoints' ? { geo: true, geoType: 'geoPoints' } : {})
          })
        }
      })
    },
    getSlugs: (component) => {
      const slugs: Array<v0.StandaloneSlug | v0.SuffixSlug> = [component.targetField as v0.StandaloneSlug];
      if (component.lookup.kind === 'Dynamo' && component.lookup.queryFields) {
        slugs.push('_decrypted' as v0.SuffixSlug);
      }
      return { slugs };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      switch (component.lookup.kind) {
        case 'Standard':
          addParent(component.targetField, { field: component.lookup.queryField, type: 'formula' }, dependencyMap);
          break;
        case 'Geo':
          addParent(component.targetField, { field: component.lookup.queryField, type: 'formula' }, dependencyMap);
          addParent(component.targetField, { field: component.lookup.queryField + '_lat', type: 'formula' }, dependencyMap);
          addParent(component.targetField, { field: component.lookup.queryField + '_lng', type: 'formula' }, dependencyMap);
          break;
        case 'GeoShape':
          addParent(component.targetField, { field: component.lookup.queryField, type: 'formula' }, dependencyMap);
          addParent(component.targetField, { field: component.lookup.queryField + '_lat', type: 'formula' }, dependencyMap);
          addParent(component.targetField, { field: component.lookup.queryField + '_lng', type: 'formula' }, dependencyMap);
          break;
        case 'GeoPoints':
          addParent(component.targetField, { field: component.lookup.queryField, type: 'formula' }, dependencyMap);
          break;
        case 'Dynamo':
          component.lookup.queryFields.forEach(field => {
            addParent(component.targetField, { field: field.fieldInAidKit, type: 'formula' }, dependencyMap);
          })
          break;
      }
    }
  },
  'Milestone': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: component.name,
          'Field Type': 'Stage',
          Translation: [add.Translation({ en: component.name } as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  },
  'Number': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Number',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': component.encrypted ? ['Formatted', 'Encrypted'] : ['Formatted'],
          Metadata: JSON.stringify(component)
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: component.encrypted ? ['_hash' as v0.SuffixSlug] : [] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    },
  },
  'NumberWithUnit': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id,
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Number With Unit',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': 'foobar', // only needed for field validation
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
          Metadata: JSON.stringify({
            questions: [
              {
                type: 'number',
                targetField: component.numberQuestion.targetField
              },
              {
                type: 'unit',
                targetField: component.unitQuestion.targetField,
                choices: component.unitQuestion.choices.map(choice => (
                  {
                    Name: choice.value,
                    ...choice.label
                  }
                ))
              },
            ],
            format: component.format,
            integers_only: component.integers_only,
          })
        }
      });
      // Hidden field hack to make RS happy
      add.Question({
        id,
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Number',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.numberQuestion.targetField,
          'Additional Options': ['Hidden'],
        }
      })
      add.Question({
        id,
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Single Select',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.unitQuestion.targetField,
          'Options (if relevant)': (component.unitQuestion.choices || []).map(add.Choice),
          'Additional Options': ['Hidden'],
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [{
      kind: 'And',
      clauses: [{
        kind: 'Exists',
        field: component.numberQuestion.targetField
      }, {
        kind: 'Exists',
        field: component.unitQuestion.targetField
      }]
    }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.numberQuestion.targetField, dependencyMap });
      addChild(component.numberQuestion.targetField, { field: component.unitQuestion.targetField, type: 'formula' }, dependencyMap);
    },
  },
  'Plaid Bank Account': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Plaid Bank Account',
          'Target Field': component.targetField,
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Translation': [add.Translation((component as any).content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify(component)
        }
      })
    },
    getSlugs: (component) => {
      return {
        slugs: [
          ...[
            '_income',
            '_error',
            '_assets_token',
            '_assets',
            '_assets_error',
            '_assets_ready'
          ] as v0.SuffixSlug[],
          'plaid_user' as v0.StandaloneSlug,
          'plaid_user_income' as v0.StandaloneSlug
        ]
      };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    },
  },
  'Plaid Income': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Plaid Income',
          'Target Field': component.targetField,
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify(component)
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    },
  },
  'Qualtrics Response Handler': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Qualtrics Response Handler',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
          Metadata: JSON.stringify(component),
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  },
  'Ranking': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Ranking',
          'Target Field': component.targetField,
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Options (if relevant)': component.choices.map(add.Choice),
          Metadata: JSON.stringify({
            num_choices: (component.number_of_required_choices || component.choices.length),
          })
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    },
  },
  'ResumeWidget': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Resume Widget',
          'English Content': '--',
          'Spanish Content': '--',
          'Metadata': JSON.stringify({
            ingestedResumesToSubsurvey: component.ingestedResumesToSubsurvey,
            customButtonText: component.customButtonText
          })
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { },
  },
  'SearchSelect': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      if ('version' in component && component.version > 0) {
        add.Question({
          id: generateUuid(id+"top1"),
          createdTime: '',
          fields: {
            Question: id,
            'Field Type': component.searchType?.kind === 'ServerSide' ? 'Search Select Large Dictionary' : 'Search Select',
            Translation: [add.Translation(component.content as v0.Text)],
            'Who Can Edit': [add.Role('Screener', ['Screener'])],
            'Target Field': component.targetField,
            'English Content': '--',
            'Spanish Content': '--',
            'Additional Options': ['Formatted'],
            Metadata: JSON.stringify({
              max_options: component.maxOptions,
              search_type: component.searchType,
            })
          }
        })
      } else {
        const legacyComponent = component as v0.LegacySearchSelectV0;
        add.Question({
          id: generateUuid(id+"top2"),
          createdTime: '',
          fields: {
            Question: id,
            'Field Type': 'Search Select',
            Translation: [add.Translation(legacyComponent.content as v0.Text)],
            'Who Can Edit': [add.Role('Screener', ['Screener'])],
            'Target Field': legacyComponent.targetField,
            'English Content': '--',
            'Spanish Content': '--',
            'Additional Options': ['Formatted'],
            Metadata: JSON.stringify({
              max_options: legacyComponent.maxOptions,
              search_type: {
                kind: 'ClientSide',
                options_file_url: legacyComponent.optionsFileUrl,
              },
            })
          }
        })
      }
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    },
  },
  'Section': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Section',
          Translation: [add.Translation(component.title as v0.Text)],
          // this metadata does not actually get passed to the scaffolding.
          Metadata: JSON.stringify({ sectionId: component.sectionId })
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      const virtualNode: DependencyNode = {
        id: `_${component.sectionId || component.title.en}_${slugid.nice()}`, // append uid to avoid collisions
        parents: [],
        children: [],
        component,
      }
      dependencyMap.set(virtualNode.id, virtualNode);
      if (component.conditionalOn) {
        connectConditionsToTopLevelComponents(component.conditionalOn, component.components, dependencyMap, virtualNode);
      } else {
        component.components.forEach(((block: DistroComponent) => {
          TRANSLATORS[block.kind as keyof typeof TRANSLATORS]?.calculateDependencies(block as any, dependencyMap);
        }));
      }
    }
  },
  'Select': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': component.multiple ? 'Multiple Select' : 'Single Select',
          Translation: [add.Translation(component.content as v0.Text)],
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          'Options (if relevant)': (component.choices || []).map(add.Choice),
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
          Metadata: JSON.stringify({
            max_allowed_selections: component.maxAllowedSelections,
            component,
          })
        }
      })
    },
    getSlugs: (component) => {
      const slugs: Array<v0.StandaloneSlug | v0.SuffixSlug> = [];
      for (const option of (component.choices || [])) {
        // Note: this has changed from its previous logic in RS Other.ts
        if (option.otherField || option.value === 'other' || option.label.en.toLowerCase() === 'other') {
          if (option.value === 'other') {
            slugs.push('_other' as v0.SuffixSlug);
          }
          if (option.otherField && option.value !== 'other') {
            slugs.push('_' + option.value as v0.SuffixSlug);
          }
        }
      }

      return { slugs };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    },
  },
  'SelectFromData': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': component.multiple ? 'Multiple Select' : 'Single Select',
          Translation: [add.Translation(component.content as v0.Text)],
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
          Metadata: JSON.stringify({
            max_allowed_selections: component.maxAllowedSelections,
            source_field: component.sourceField,
          })
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      addParent(component.targetField, { field: component.sourceField, type: 'formula' }, dependencyMap);
    },
  },
  'State Selector': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'State Selector',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': ['Formatted'],
          Metadata: JSON.stringify(component),
        }
      })
    },
    getSlugs: (component) => {
      return {
        slugs: [
          "_code" as v0.StandaloneSlug,
        ]
      };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    }
  },
  'Show Field': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Show Field',
          Translation: [add.Translation(component.headerText as v0.Text)],
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          'Additional Options': [
            ...(component.showsVersions ? ['Shows Versions'] : []),
            ...(component.allowDecrypting ? ['Allow Decrypting'] : []),
            ...(component.showFullUrls ? ['Show Full Urls'] : []),
            ...(component.renderPublicImages ? ['Render Public Images'] : []),
            ...(component.pinToLeftColumn ? ['Pin to Left Column'] : []),
          ],
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify({
            keepVisibleForReview: component.keepVisibleForReview,
            showInlineAlerts: component.showInlineAlerts,
            beautifyJSON: component.beautifyJSON,
          }),
        }
      })
    },
    getSlugs: (component) => {
      return {
        slugs: [
          ...(component.allowDecrypting ? ['_viewed_by'] : [])
        ] as v0.SuffixSlug[]
      };
    },
    getRequirements: (component) => [],
    // TODO(Riley): I think for now at least, it makes sense to consider ShowFields as irrelevant as far as dependencies go
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  },
  'Similar Document Review': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Similar Document Review',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          ...(component.content ? { Translation: [add.Translation(component.content as v0.Text)] } : {}),
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify(component),
        }
      })
    },
    getSlugs: (component) => {
      return {
        slugs: [
          '_documentid' as v0.SuffixSlug
        ]
      };
    },
    getRequirements: (component) => [{
      kind: 'Or',
      clauses: [{
        kind: 'Not',
        clause: {
          kind: 'Exists',
          field: component.documentField
        }
      }, {
        kind: 'Exists',
        field: component.targetField
      }]
    }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      addParent(component.targetField, { field: component.documentField, type: 'formula' }, dependencyMap);
      if (component.comparisonFields) {
        component.comparisonFields.forEach((comparisonField) => {
          addParent(component.targetField, { field: comparisonField.field, type: 'formula' }, dependencyMap);
        })
      }
    },
  },
  'Similar Document Check': {
    addFunc: (component, add: Add) => {
      if (component.showStatus) {
        const id = generateUuid(component);
        add.Question({
          id: generateUuid(id+"top"),
          createdTime: '',
          fields: {
            Question: id,
            'Field Type': 'Similar Document Check',
            'Who Can Edit': [add.Role('Screener', ['Screener'])],
            'Target Field': component.targetField,
            'English Content': '--',
            'Spanish Content': '--',
            Metadata: JSON.stringify(component),
          }
        })
      }
    },
    getSlugs: (component) => {
      return { slugs: ['_timestamp', '_depshash'] as v0.SuffixSlug[] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      component.documentTextFields.forEach((documentTextField) => {
        addParent(component.targetField, { field: documentTextField, type: 'formula' }, dependencyMap);
      })
    },
  },
  'SubmitButton': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Submit Button',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify({
            custom_text: component.customText,
            hide_request_changes_button: component.hideRequestChangesButton,
          })
        },
      })
    },
    getSlugs: (component) => {
      return { slugs: ['_server_time'] as v0.SuffixSlug[] }
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
    },
  },
  'Subsurvey': {
    addFunc: (component, add: Add) => {
      let sections = component.sections.map((s) => add.Section(s, add.Depth + 1));
      // Add the final subsurvey question

      const expiration = component.defaultExpiration;
      let legacyExpiration: [number, 'days'] = [365, 'days'];
      if (expiration) {
        legacyExpiration = [expiration.days + expiration.weeks * 7 + expiration.months * 30, 'days'];
      }

      add.Question({
        id: generateUuid(component),
        createdTime: '',
        fields: {
          Question: component.path,
          ...(component.title ? { Translation: [add.Translation(component.title as v0.Text)] } : {}),
          'Field Type': 'Subsurvey',
          'English Content': '--',
          'Spanish Content': '--',
          Subquestions: sections,
          Metadata: JSON.stringify({
            autofill_sections: true,
            needs_key: component.anonymous ? false : true,
            conditional: 'false',
            default_expiration: legacyExpiration,
            custom_submitted_modal: component.customSubmittedModal,
            branding: component.branding,
            should_fast_forward: component.shouldFastForward,
            hideFromNavigation: component.hideFromNavigation,
            block_further_edits: component.blockFurtherEdits,
            accessConditional: component.accessConditional,
            sectionBottomButtons: component.sectionBottomButtons,
            warnOnIncompleteSurvey: component.warnOnIncompleteSurvey,
          })
        }
      });
    },
    getSlugs: (component) => {
      const slugs = [];
      if (component.protectedSearch?.assigns) {
        slugs.push(component.protectedSearch.assigns);
      }
      if (component.protectedSearch) {
        slugs.push('resend_survey_' + component.path);
      }
      return { slugs: slugs as v0.StandaloneSlug[] };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      component.sections.forEach((section => {
        TRANSLATORS[section.kind].calculateDependencies(section, dependencyMap);
        // TODO(Riley): how do we want to handle the relationship between fields used in Protected Search
        // and those used inside the Subsurvey?
      }));
    }
  },
  'SupportButton': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Support Button',
          Translation: [
            add.Translation(component.messageTextOverride as v0.Text),
            add.Translation(component.buttonTextOverride as v0.Text),
          ],
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify(component)
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { },
  },
  'TextEntry': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      let fieldType = 'Single Line Text Entry';
      const additionalOptions = ['Formatted'];
      if (component.format === 'legalName') {
        additionalOptions.push('Legal Name');
      } else if (component.format === 'email') {
        fieldType = 'Email';
      } else if (component.format === 'phone') {
        fieldType = 'Phone Number';
      } else if (component.multiline) {
        fieldType = 'Multiline Text Entry';
      }
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': fieldType,
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          'Additional Options': additionalOptions,
          Metadata: JSON.stringify({
            min_length: component.minLength,
            max_length: component.maxLength,
            confirmation_field: component.confirmationField, // for phone and email questions
          })
        }
      })
    },
    getSlugs: (component) => {
      return { slugs: [] };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      if (component.confirmationField) {
        addParent(component.targetField, { type: 'formula', field: component.confirmationField }, dependencyMap);
      }
    },
  },
  'Usio Mailing': {
    addFunc: (component, add: Add) => {
      add.Question({
        id: generateUuid(component),
        createdTime: '',
        fields: {
          Question: 'Usio Mailing',
          'Field Type': 'Payment',
          'Target Field': component.targetField,
          'English Content': '--',
          'Spanish Content': '--',
          'Metadata': JSON.stringify({
            amount: '0',
            type: 'usio_mail',
            address: component.mailingAddress,
            sql: CompileExpressionToSQL({ cond: component.condition.expr, orderBy: component.condition.orderBy }),
            auto_associate: component.autoAssociate
          }),
        }
      });
    },
    getSlugs: (component) => {
      return {
        slugs: ['_issued' as v0.SuffixSlug, '_card_id' as v0.SuffixSlug]
      };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      addParent(component.targetField, { type: 'formula', field: component.mailingAddress }, dependencyMap);

      (new Set(safeGetFormulaParents(CompileExpressionToJS(component.condition.expr)))).forEach((p) => {
        addParent(component.targetField, { field: p, type: 'conditional' }, dependencyMap);
      })
    },
  },
  'Validated': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Validated',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'Target Field': component.targetField,
          Translation: [add.Translation(component.content as v0.Text)],
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify({
            formula: (component.formula as any) || '',
            navigateBackToSection: component.navigateBackToSection,
          }),
          'Additional Options': [
            ...(component.advisory === true ? ['Advisory'] : []),
            ...(component.showAsAlert ? [`Alert:${component.showAsAlert.variant}`, ...(component.showAsAlert.dismissible ? ['Alert:dismissible'] : []), ...(component.showAsAlert.iconName ? [`Alert:icon:${component.showAsAlert.iconName}`] : [])] : [])
          ]
        }
      })
    },
    getSlugs: (component) => {
      return {
        slugs: ['_error' as v0.SuffixSlug]
      };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });

      (new Set(safeGetFormulaParents(component.formula))).forEach((p) => {
        addParent(component.targetField, { field: p, type: 'formula' }, dependencyMap);
      });
    },
  },
  'Related Applicants': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Related Applicants',
          'Who Can Edit': [add.Role('Screener', ['Screener'])],
          'English Content': '--',
          'Spanish Content': '--',
          Metadata: JSON.stringify(component),
        }
      })
    },
    getSlugs: () => ({ slugs: [] }),
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  },
  'Third Party Check': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id: generateUuid(id+"top"),
        createdTime: '',
        fields: {
          Question: id,
          'Field Type': 'Third Party Check',
          'English Content': '--',
          'Spanish Content': '--',
        }
      })
    },
    getSlugs: (component: v0.ThirdPartyCheck) => { 
      const slugs: Array<v0.StandaloneSlug | v0.SuffixSlug> = [
        '_status',
        '_depshash',
        '_depshash1',
        '_depshash2',
        '_error'
      ] as v0.SuffixSlug[]
      if (component.type.kind === 'Analyze ID') {
        for (const key in analyzeOutputSchema) {
          slugs.push(`_${key}` as v0.SuffixSlug);
        }
      }
      if (component.type.kind === 'Document Signals') {
        for(const field of component.type.attachmentFields) {
          slugs.push(`${field}_${component.targetField}` as v0.StandaloneSlug);
        }
      }

      return { slugs };
    },
    getRequirements: (component) => [{ kind: 'Exists', field: component.targetField }],
    calculateDependencies: (component, dependencyMap: DependencyMap) => {
      addConditionsAndSlugsAndContentDeps({ component, mainTargetField: component.targetField, dependencyMap });
      if (component.statusField) {
        addChild(component.targetField, { type: 'formula', field: component.statusField }, dependencyMap);
      }

      switch (component.type.kind) {
        case 'Analyze ID':
          addParent(component.targetField, { type: 'formula', field: component.type.idField }, dependencyMap);
          break;
        case 'IP VPN Check':
          addParent(component.targetField, { type: 'formula', field: component.type.ipField || '_ip' }, dependencyMap);
          break;
        case 'Email Domain Check':
          addParent(component.targetField, { type: 'formula', field: component.type.emailField }, dependencyMap);
          break;
        case 'ID Check':
          addParent(component.targetField, { type: 'formula', field: component.type.detailsField }, dependencyMap);
          break;
        case 'ITIN Check':
          addParent(component.targetField, { type: 'formula', field: component.type.itinField }, dependencyMap);
          if (component.type.nameField) {
            addParent(component.targetField, { type: 'formula', field: component.type.nameField }, dependencyMap);
          }
          break;
        case 'Program Specific':
          if (component.type.dependencies) {
            component.type.dependencies.forEach(dep => {
              addParent(component.targetField, { type: 'formula', field: dep }, dependencyMap);
            });
          }
          break;
        case 'Shared Key Computation':
          // TODO(Riley): the dependencies for component.type.propagateUpdates is tricky because typically fields in formulas are parents
          // to the component target field, but the jsdoc for the property says "...generally used to set/update multiple other fields"
          // which would mean those fields should be dependents of this component, not the other way around.

          component.type.dependsOn.forEach((field => {
            addParent(component.targetField, { type: 'formula', field: field.field }, dependencyMap);
          }));
          break;
        case 'Document Signals':
          component.type.attachmentFields.forEach(field => {
            addChild(field, { type: 'formula', field: field + '_' + component.targetField }, dependencyMap);
          });
          break;
      }

      (new Set(safeGetFormulaParents(CompileExpressionToJS(component.condition)))).forEach((p) => {
        addParent(component.targetField, { field: p, type: 'conditional' }, dependencyMap);
      })
    },
  },
  'Image Preview': {
    addFunc: (component, add: Add) => {
      const id = generateUuid(component);
      add.Question({
        id,
        createdTime: '',
        fields: {
          "Field Type": 'Image Preview',
          Question: id,
          Metadata: JSON.stringify({ component })
        }
      })
    },
    getSlugs: () => ({ slugs: [] }),
    getRequirements: (component) => [],
    calculateDependencies: (component, dependencyMap: DependencyMap) => { }
  }
}
